import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatarReais } from '@reducee/contracts';
import { useRef, useState, type FormEvent } from 'react';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import {
  Aviso,
  CabecalhoDePagina,
  CabecalhoDeTabela,
  Cartao,
  CLASSE_TABELA,
  Indicador,
} from '../componentes/Superficie.tsx';
import { CampoSelecao, CampoTexto } from '../componentes/Campo.tsx';
import { useOrigens } from '../dominio/origens.ts';

type LinhaDaPrevia = {
  numero: number;
  situacao: 'VALIDA' | 'ERRO' | 'DUPLICADA';
  nome: string | null;
  telefone: string | null;
  email: string | null;
  produto: string | null;
  tipoContrato: string | null;
  valorMensalCentavos: number | null;
  score: string | null;
  origem: string | null;
  campanha: string | null;
  erro: string | null;
  contatoExistente: { id: string; nome: string } | null;
};

type Previa = {
  importacaoId: string;
  arquivoNome: string;
  colunasIgnoradas: string[];
  linhasLidas: number;
  validas: number;
  duplicadas: number;
  comErro: number;
  linhas: LinhaDaPrevia[];
};

type Resultado = {
  importacaoId: string;
  linhasLidas: number;
  importados: number;
  duplicados: number;
  comErro: number;
  cadenciaIniciada: boolean;
};

type Historico = {
  id: string;
  tipo: 'LEADS' | 'CARTEIRA';
  arquivoNome: string;
  status: string;
  linhasLidas: number;
  importados: number;
  duplicados: number;
  comErro: number;
  criadaEm: string;
  usuaria: { nome: string };
};

const ROTULO_SITUACAO: Record<LinhaDaPrevia['situacao'], string> = {
  VALIDA: 'Válida',
  ERRO: 'Erro',
  DUPLICADA: 'Duplicada',
};

/** O cabeçalho que o leitor de planilha reconhece, na ordem do modelo oficial. */
// A ordem agrupa o que é da pessoa, o que é do consentimento e o que é do
// contrato. `consentimento` e `podeReceberContato` estavam de fora do modelo
// embora o importador já os aceitasse: são os dois campos que registram o
// aceite, e sem eles a planilha não carrega a base legal do contato.
const COLUNAS_DO_MODELO = [
  'nome',
  'cpfCnpj',
  'telefone',
  'whatsapp',
  'email',
  'consentimento',
  'podeReceberContato',
  'origem',
  'campanha',
  'produto',
  'operadora',
  'tipoContrato',
  'valorMensal',
  'observacoes',
];

/**
 * Importação de listas comerciais.
 *
 * Duas etapas de propósito: a prévia mostra erros e duplicidades antes de
 * qualquer gravação. Importar uma lista errada é caro de desfazer.
 *
 * A decisão sobre cadência é tomada aqui, no passo 1, e não depois da prévia:
 * é uma escolha sobre a lista inteira, e quem monta a importação já sabe se
 * aquela lista deve entrar em contato automático.
 */
export function PaginaImportarLeads(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const campoArquivo = useRef<HTMLInputElement>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [iniciarCadencia, setIniciarCadencia] = useState(false);
  const origens = useOrigens();

  const historico = useQuery({
    queryKey: ['importacoes'],
    queryFn: () => chamarApi<Historico[]>('/importacoes'),
    // A carteira tem tela própria, e o histórico dela mora lá.
    select: (todas) => todas.filter((importacao) => importacao.tipo === 'LEADS'),
  });

  const tratarErro = (causa: unknown): void => {
    setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível ler o arquivo.');
  };

  const enviar = useMutation({
    mutationFn: async (campos: FormData) => {
      // FormData precisa ir sem content-type definido por nós: o navegador
      // monta o boundary do multipart.
      const resposta = await fetch('/api/importacoes/previa', {
        method: 'POST',
        credentials: 'same-origin',
        body: campos,
      });

      const dados = (await resposta.json().catch(() => null)) as Record<string, unknown> | null;
      if (!resposta.ok) {
        throw new ErroDaApi(
          resposta.status,
          typeof dados?.['mensagem'] === 'string' ? dados['mensagem'] : 'Falha ao ler o arquivo.',
          dados,
        );
      }
      return dados as unknown as Previa;
    },
    onSuccess: async (dados) => {
      setPrevia(dados);
      setResultado(null);
      setErro(null);
      // A prévia já grava a importação com status de prévia: sem invalidar,
      // o card "Últimas importações" continuaria mostrando a lista velha.
      await clienteDeConsultas.invalidateQueries({ queryKey: ['importacoes'] });
    },
    onError: tratarErro,
  });

  const confirmar = useMutation({
    mutationFn: (id: string) =>
      chamarApi<Resultado>(`/importacoes/${id}/confirmar`, {
        metodo: 'POST',
        corpo: { iniciarCadencia },
      }),
    onSuccess: async (dados) => {
      setResultado(dados);
      setPrevia(null);
      setErro(null);
      if (campoArquivo.current) campoArquivo.current.value = '';
      await clienteDeConsultas.invalidateQueries({ queryKey: ['importacoes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['leads'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });
    },
    onError: tratarErro,
  });

  function aoLerArquivo(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const arquivo = campoArquivo.current?.files?.[0];
    if (!arquivo) {
      setErro('Escolha um arquivo antes de validar.');
      return;
    }
    setErro(null);
    const campos = new FormData(evento.currentTarget);
    campos.set('arquivo', arquivo);
    enviar.mutate(campos);
  }

  /**
   * O modelo sai do próprio leitor: baixar um cabeçalho que a importação não
   * reconhece seria pior que não oferecer modelo nenhum.
   */
  function baixarModelo(): void {
    // O \ufeff é a marca que o Excel procura para abrir o arquivo como UTF-8.
    // Sem ela, ele assume a codificação do sistema e os acentos chegam trocados.
    const conteudo = `\ufeff${COLUNAS_DO_MODELO.join(';')}\n`;
    const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));
    const ancora = document.createElement('a');
    ancora.href = url;
    ancora.download = 'modelo-reducee-leads.csv';
    ancora.click();
    URL.revokeObjectURL(url);
  }

  // A prévia e o resultado contam as mesmas coisas com nomes diferentes.
  const contagens = {
    linhasLidas: previa?.linhasLidas ?? resultado?.linhasLidas ?? 0,
    prontos: previa?.validas ?? resultado?.importados ?? 0,
    duplicados: previa?.duplicadas ?? resultado?.duplicados ?? 0,
    comErro: previa?.comErro ?? resultado?.comErro ?? 0,
  };

  return (
    <section>
      <CabecalhoDePagina
        area="Reducee Sales"
        titulo="Importar Leads"
        descricao="Importe listas em Excel (.xlsx) ou CSV com validação antes de gravar na base."
      />

      <Aviso tom="sucesso">
        <b>Regra de distribuição:</b> todos os leads importados entram inicialmente para a
        Administradora. A Comercial só recebe leads depois, por direcionamento manual.
      </Aviso>

      {erro ? (
        <p role="alert" className="mb-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao
          titulo="1. Selecionar arquivo"
          descricao="Use o modelo Reducee para reduzir erros de preenchimento."
        >
          <form onSubmit={aoLerArquivo}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="campo-planilha" className="text-[11px] font-extrabold text-[#536575]">
                  Arquivo Excel ou CSV
                </label>
                <input
                  id="campo-planilha"
                  ref={campoArquivo}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  required
                  className="mt-1.25 w-full rounded-[9px] border border-[#d7e0e6] bg-superficie px-2.75 py-2.5 text-sm"
                />
              </div>

              <CampoSelecao
                rotulo="Iniciar cadência após importar?"
                name="iniciarCadencia"
                value={iniciarCadencia ? 'sim' : 'nao'}
                onChange={(evento) => setIniciarCadencia(evento.target.value === 'sim')}
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </CampoSelecao>

              <CampoSelecao
                key={origens.pronta ? 'origens-prontas' : 'origens-carregando'}
                rotulo="Origem padrão"
                name="origemPadrao"
                defaultValue="REDUCEE"
                dica="Vale para as linhas sem coluna de origem."
              >
                {origens.ativas.map((origem) => (
                  <option key={origem.codigo} value={origem.codigo}>
                    {origem.nome}
                  </option>
                ))}
              </CampoSelecao>

              <CampoTexto
                rotulo="Campanha padrão"
                name="campanhaPadrao"
                placeholder="Ex.: META Setembro 2026"
                className="md:col-span-2"
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={enviar.isPending}
                className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
              >
                {enviar.isPending ? 'Lendo o arquivo…' : 'Ler e validar arquivo'}
              </button>
              <button
                type="button"
                onClick={baixarModelo}
                className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
              >
                Baixar modelo CSV
              </button>
            </div>

            <p className="mt-3 text-[12px] text-texto-suave">
              Mantenha os títulos da primeira linha do modelo oficial. Colunas reconhecidas:{' '}
              {COLUNAS_DO_MODELO.join(', ')}.
            </p>
          </form>
        </Cartao>

        <Cartao titulo="Regras da importação" descricao="Aplicadas automaticamente antes de salvar.">
          <dl className="divide-y divide-linha">
            <Regra termo="Responsável inicial" valor="Administradora" />
            <Regra termo="Etapa inicial" valor="Lead" />
            <Regra termo="Duplicidade" valor="Telefone, WhatsApp, e-mail e CPF/CNPJ" />
            <Regra termo="Score" valor="Bronze · Prata · Ouro · Diamante" />
            <Regra termo="Sem contato válido" valor="Não importa" />
            <Regra termo="Duplicado" valor="Ignorado" />
          </dl>
        </Cartao>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Indicador rotulo="Linhas lidas" valor={String(contagens.linhasLidas)} />
        <Indicador rotulo="Prontos para importar" valor={String(contagens.prontos)} />
        <Indicador rotulo="Duplicados" valor={String(contagens.duplicados)} />
        <Indicador rotulo="Com erro" valor={String(contagens.comErro)} />
        <Indicador rotulo="Cadência" valor={iniciarCadencia ? 'Sim' : 'Não'} />
      </div>

      {resultado ? (
        <div
          data-teste="resultado-importacao"
          className="mt-4 rounded-xl border border-sucesso/30 bg-sucesso/5 p-5"
        >
          <h2 className="text-base font-[850] text-marinho">Importação concluída</h2>
          <p className="mt-1 text-[12px] text-texto-suave">
            {resultado.cadenciaIniciada
              ? 'Cadência iniciada para as Oportunidades criadas.'
              : 'Nenhuma cadência foi iniciada.'}
          </p>
        </div>
      ) : null}

      <Cartao
        className="mt-4"
        titulo="Prévia da importação"
        descricao="Revise os registros antes de concluir. Nenhum lead é salvo apenas ao ler o arquivo."
        acao={
          // O protótipo mantém o botão na tela desde o começo, desabilitado até
          // haver o que confirmar. Escondê-lo até a leitura do arquivo apaga da
          // tela qual é o próximo passo.
          <button
            type="button"
            onClick={() => {
              if (previa) confirmar.mutate(previa.importacaoId);
            }}
            disabled={!previa || confirmar.isPending || previa.validas === 0}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
          >
            Confirmar importação
          </button>
        }
      >
        <div>
          {previa && previa.colunasIgnoradas.length > 0 ? (
            <p className="mb-3 text-[12px] text-texto-suave">
              Colunas ignoradas: {previa.colunasIgnoradas.join(', ')}
            </p>
          ) : null}

          <div className="overflow-x-auto">
            <table className={CLASSE_TABELA}>
              <CabecalhoDeTabela
                colunas={[
                  'Linha',
                  'Lead',
                  'Contato',
                  'Contrato',
                  'Valor',
                  'Score',
                  'Origem/Campanha',
                  'Status',
                ]}
              />
              {/* O marcador de teste vive no corpo da tabela, e não no cartão:
                  ele precisa continuar significando "existe prévia", agora que
                  a tabela é desenhada antes da leitura do arquivo. */}
              <tbody data-teste={previa ? 'previa-importacao' : undefined}>
                {previa ? (
                  previa.linhas.map((linha) => (
                    <tr
                      key={linha.numero}
                      data-teste="linha-previa"
                      className="border-b border-linha last:border-0"
                    >
                      <td className="px-3.25 py-3">{linha.numero}</td>
                      <td className="px-3.25 py-3 font-medium">{linha.nome ?? '—'}</td>
                      <td className="px-3.25 py-3">{linha.telefone ?? linha.email ?? '—'}</td>
                      <td className="px-3.25 py-3">
                        {linha.produto ?? '—'}
                        {linha.tipoContrato ? (
                          <span className="block text-xs text-texto-suave">{linha.tipoContrato}</span>
                        ) : null}
                      </td>
                      <td className="px-3.25 py-3">
                        {linha.valorMensalCentavos ? formatarReais(linha.valorMensalCentavos) : '—'}
                      </td>
                      <td className="px-3.25 py-3">{linha.score ?? '—'}</td>
                      <td className="px-3.25 py-3">
                        {linha.origem ?? '—'}
                        {linha.campanha ? (
                          <span className="block text-xs text-texto-suave">{linha.campanha}</span>
                        ) : null}
                      </td>
                      <td className="px-3.25 py-3">
                        <span
                          className={
                            linha.situacao === 'VALIDA'
                              ? 'font-medium text-sucesso'
                              : linha.situacao === 'ERRO'
                                ? 'font-medium text-erro'
                                : 'font-medium text-alerta'
                          }
                        >
                          {ROTULO_SITUACAO[linha.situacao]}
                        </span>
                        {linha.erro ? (
                          <span className="block text-xs text-texto-suave">{linha.erro}</span>
                        ) : null}
                        {linha.contatoExistente ? (
                          <span className="block text-xs text-texto-suave">
                            Já existe: {linha.contatoExistente.nome}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-4.5 text-[12px] text-texto-suave">
                      Selecione um arquivo para começar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Cartao>

      <Cartao className="mt-4" titulo="Últimas importações">
        {historico.data && historico.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={CLASSE_TABELA}>
              <CabecalhoDeTabela
                colunas={['Arquivo', 'Quando', 'Lidas', 'Importados', 'Duplicados', 'Erros', 'Situação']}
              />
              <tbody>
                {historico.data.map((importacao) => (
                  <tr key={importacao.id} className="border-b border-linha last:border-0">
                    <td className="px-3.25 py-3 font-medium">{importacao.arquivoNome}</td>
                    <td className="px-3.25 py-3">
                      {new Date(importacao.criadaEm).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3.25 py-3">{importacao.linhasLidas}</td>
                    <td className="px-3.25 py-3">{importacao.importados}</td>
                    <td className="px-3.25 py-3">{importacao.duplicados}</td>
                    <td className="px-3.25 py-3">{importacao.comErro}</td>
                    <td className="px-3.25 py-3">{importacao.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-4.5 text-[12px] text-texto-suave">Nenhuma importação registrada.</p>
        )}
      </Cartao>
    </section>
  );
}

/** Uma linha do quadro de regras: o que é, e o que a importação faz com isso. */
function Regra({ termo, valor }: { termo: string; valor: string }): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 py-2">
      <dt className="text-[12px] text-texto-suave">{termo}</dt>
      <dd className="text-[13px] font-[750] text-marinho">{valor}</dd>
    </div>
  );
}
